import type { SvgIconComponent } from 'components/icons';
import { type ReactNode, useId } from 'react';
import { cn } from 'utils/cn';

export interface FigureHeaderProps {
    icon: SvgIconComponent;
    /** The tile's pair: a family's, from `familyTileClass`, or the neutral tile. */
    tileClassName: string;
    title?: string;
    /** The line under the title: the count and the sort, or what the Figure asks. */
    meta?: ReactNode;
    /** At the header's end: Download, Open full size or Close, or a Figure's own. */
    actions?: ReactNode;
    titleId?: string;
}

/** A Figure's header: its tile, its title and meta line, its actions. The full-size dialog draws the same one. */
export function FigureHeader({ icon: Icon, tileClassName, title, meta, actions, titleId }: FigureHeaderProps) {
    return (
        <div className="flex items-center gap-3 px-4 py-3">
            <span
                aria-hidden="true"
                className={cn('grid size-9 shrink-0 place-items-center rounded-[10px]', tileClassName)}
            >
                <Icon className="size-[18px]" />
            </span>
            <div className="min-w-0 flex-1">
                {title ? (
                    <div id={titleId} className="text-section text-foreground text-balance">
                        {title}
                    </div>
                ) : null}
                {meta ? <div className="text-caption text-foreground-tertiary">{meta}</div> : null}
            </div>
            {actions ? <div className="flex items-center gap-1">{actions}</div> : null}
        </div>
    );
}

type Props = Omit<FigureHeaderProps, 'titleId'> & {
    children?: ReactNode;
    /** While its rows load: screen readers wait for them. */
    busy?: boolean;
    className?: string;
};

/**
 * The card every Figure is drawn in: a table, a list, a chart, a brief, a choice. Its body is the caller's; a container,
 * so the body lays itself out by the Figure's own width (the panel, the page's column, the phone).
 */
export function FigureFrame({ children, busy, className, ...header }: Props) {
    const titleId = useId();
    return (
        <section
            aria-labelledby={header.title ? titleId : undefined}
            aria-busy={busy || undefined}
            className={cn(
                'border-border bg-card new:border-0 new:shadow-card new:outline-hidden @container overflow-hidden rounded-xl border',
                className
            )}
        >
            <FigureHeader {...header} titleId={titleId} />
            {children}
        </section>
    );
}
